const NON_SELECTABLE_TYPES = new Set([
  'checkbox',
  'radio',
  'file',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'image',
  'hidden',
]);

export interface InputAutoSelectState {
  wasFullySelectedOnMouseDown: boolean;
  activeInputOnMouseDown: HTMLInputElement | null;
  isMouseDownActive: boolean;
}

export function createInputAutoSelectState(): InputAutoSelectState {
  return {
    wasFullySelectedOnMouseDown: false,
    activeInputOnMouseDown: null,
    isMouseDownActive: false,
  };
}

export function isSelectableTextInput(el: unknown): el is HTMLInputElement {
  if (!el || typeof el !== 'object') return false;
  const input = el as HTMLInputElement;
  if (input.tagName !== 'INPUT') return false;
  if (NON_SELECTABLE_TYPES.has(input.type)) return false;
  if (input.readOnly || input.disabled) return false;
  if (typeof input.getAttribute === 'function' && input.getAttribute('data-auto-select') === 'false') {
    return false;
  }
  return true;
}

export function handleInputMouseDown(
  target: unknown,
  state: InputAutoSelectState,
): void {
  state.isMouseDownActive = true;
  if (isSelectableTextInput(target)) {
    state.activeInputOnMouseDown = target;
    const val = target.value;
    if (
      val &&
      target.selectionStart === 0 &&
      target.selectionEnd === val.length
    ) {
      state.wasFullySelectedOnMouseDown = true;
    } else {
      state.wasFullySelectedOnMouseDown = false;
    }
  } else {
    state.activeInputOnMouseDown = null;
    state.wasFullySelectedOnMouseDown = false;
  }
}

export function handleInputClick(
  target: unknown,
  state: InputAutoSelectState,
): boolean {
  state.isMouseDownActive = false;
  if (!isSelectableTextInput(target)) return false;
  if (!target.value) return false;

  // Nếu trước khi click, text đã được bôi đen toàn bộ rồi -> click lần này nhả bôi đen để người dùng đặt con trỏ tại vị trí click
  if (target === state.activeInputOnMouseDown && state.wasFullySelectedOnMouseDown) {
    state.wasFullySelectedOnMouseDown = false;
    return false;
  }

  // Nếu người dùng vừa kéo chuột để bôi đen 1 đoạn (selectionStart !== selectionEnd) -> giữ nguyên đoạn họ chọn
  if (
    target.selectionStart !== null &&
    target.selectionEnd !== null &&
    target.selectionStart !== target.selectionEnd
  ) {
    return false;
  }

  // Bôi đen toàn bộ data trong input
  try {
    target.select();
    return true;
  } catch {
    return false;
  }
}

export function handleInputFocusIn(
  target: unknown,
  state: InputAutoSelectState,
): boolean {
  if (!isSelectableTextInput(target)) return false;
  if (!target.value) return false;

  // Nếu được focus bằng bàn phím (Tab) - tức không phải đang trong mousedown của chuột
  if (!state.isMouseDownActive) {
    try {
      target.select();
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Tự động bôi đen toàn bộ dữ liệu khi click vào ô input có sẵn data.
 * - Click lần 1: Bôi đen toàn bộ dữ liệu.
 * - Click lần 2 khi đã bôi đen: Cho phép đặt con trỏ tại vị trí click để sửa nhanh.
 * - Quét chuột (drag): Giữ nguyên đoạn người dùng chọn.
 * - Phím Tab: Bôi đen toàn bộ dữ liệu khi di chuyển qua input.
 */
export function setupInputAutoSelect(): () => void {
  if (typeof document === 'undefined') return () => {};

  const state = createInputAutoSelectState();

  const onMouseDown = (e: MouseEvent) => {
    handleInputMouseDown(e.target, state);
  };

  const onClick = (e: MouseEvent) => {
    handleInputClick(e.target, state);
  };

  const onFocusIn = (e: FocusEvent) => {
    handleInputFocusIn(e.target, state);
  };

  document.addEventListener('mousedown', onMouseDown, true);
  document.addEventListener('click', onClick, true);
  document.addEventListener('focusin', onFocusIn, true);

  return () => {
    document.removeEventListener('mousedown', onMouseDown, true);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('focusin', onFocusIn, true);
  };
}
